/**
 * Texto sob o timbrado (acima do título "RECEITUÁRIO…"), vindo de
 * `prescription_header_text`, do prescritor da receita ou do responsável técnico.
 */

export type PrescriptionHeaderSource = {
  professional_name?: string | null;
  professional_specialty?: string | null;
  professional_registration_type?: string | null;
  professional_registration?: string | null;
  professional_registration_state?: string | null;
  professional_credentials?: string[] | null;
};

function buildSpecialtyTitleLine(specialtyRaw: string): string {
  if (/psiquiatr/i.test(specialtyRaw)) return 'Médico Psiquiatra';
  if (!specialtyRaw) return '';
  return /^médico\b/i.test(specialtyRaw) ? specialtyRaw : `Médico ${specialtyRaw}`;
}

function buildCouncilLine(regType: string, regState: string, regNum: string): string {
  const type = (regType || 'CRM').trim();
  const state = regState.trim();
  const num = regNum.trim();
  if (state) return `${type}/${state} ${num}`.trim();
  return [type, num].filter(Boolean).join(' ');
}

function extractRqeFromSpecialty(specialtyRaw: string): string {
  const rqeMatch = specialtyRaw.match(/RQE\s*(\d+)/i);
  return rqeMatch ? ` RQE ${rqeMatch[1]}` : '';
}

function buildPrescriptionHeaderFromResponsible(clinic: any): string {
  const name = String(clinic?.responsible_name ?? '').trim();
  if (!name) return '';

  const specialtyRaw = String(clinic?.responsible_specialty ?? '').trim();
  const titleLine = buildSpecialtyTitleLine(specialtyRaw);
  const crm = buildCouncilLine(
    String(clinic?.responsible_registration_type || 'CRM'),
    String(clinic?.responsible_registration_state || ''),
    String(clinic?.responsible_registration || ''),
  );
  const rqe = extractRqeFromSpecialty(specialtyRaw);

  const line1 = titleLine ? `${name} - ${titleLine}` : name;
  const line2 = `${crm}${rqe}`.trim();
  return line2 ? `${line1}\n${line2}` : line1;
}

export function buildPrescriptionHeaderFromProfessional(prescription: PrescriptionHeaderSource | null | undefined): string {
  const name = String(prescription?.professional_name ?? '').trim();
  if (!name) return '';

  const specialtyRaw = String(prescription?.professional_specialty ?? '').trim();
  let titleLine = buildSpecialtyTitleLine(specialtyRaw);
  const credentials = Array.isArray(prescription?.professional_credentials)
    ? prescription!.professional_credentials!.filter(Boolean)
    : [];
  if (!titleLine && credentials.length) titleLine = String(credentials[0]).trim();

  const crm = buildCouncilLine(
    String(prescription?.professional_registration_type || 'CRM'),
    String(prescription?.professional_registration_state || ''),
    String(prescription?.professional_registration || ''),
  );
  const rqe = extractRqeFromSpecialty(specialtyRaw);

  const line1 = titleLine ? `${name} - ${titleLine}` : name;
  const line2 = `${crm}${rqe}`.trim();
  return line2 ? `${line1}\n${line2}` : line1;
}

export function isLegacySingleLinePrescriptionHeader(text: string): boolean {
  const t = String(text || '').trim();
  if (!t || t.includes('\n')) return false;
  if (/médico\s+psiquiatra/i.test(t)) return false;
  return /CRM\/[A-Z]{2}/i.test(t) && /RQE/i.test(t);
}

export function resolvePrescriptionHeaderText(
  clinic: any,
  prescription?: PrescriptionHeaderSource | null,
): string {
  const fromPrescriber = buildPrescriptionHeaderFromProfessional(prescription);
  if (fromPrescriber) return fromPrescriber;

  const custom = String(clinic?.prescription_header_text ?? '').trim();
  const rebuilt = buildPrescriptionHeaderFromResponsible(clinic);

  if (custom && isLegacySingleLinePrescriptionHeader(custom) && rebuilt) {
    const legacyRqe = custom.match(/RQE\s*(\d+)/i);
    if (legacyRqe && !/RQE/i.test(rebuilt)) {
      const lines = rebuilt.split('\n');
      if (lines.length > 1) {
        lines[1] = `${lines[1]} RQE ${legacyRqe[1]}`.trim();
        return lines.join('\n');
      }
    }
    return rebuilt;
  }
  if (custom) return custom;
  return rebuilt;
}
